import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Outlet,
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { isMobile } from "src/utils/isMobile";
import { lang } from "src/language/Strings";
import { globalNavigator } from "src/utils/globalNavigator";
import { routes } from "src/routes";
import { useProjectVariationById } from "src/api/hooks/useProjectVariationById";
import { useQueryClient } from "@tanstack/react-query";

export default function NavigatedLayout() {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const mobile = isMobile();
  const { documentId, signingSessionId } = useParams();

  useEffect(() => {
    const updateStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", updateStatus);
    window.addEventListener("offline", updateStatus);
    globalNavigator.navigate = nav;
    return () => {
      window.removeEventListener("online", updateStatus);
      window.removeEventListener("offline", updateStatus);
    };
  }, []);
  const location = useLocation();
  const { companyId, inspectionId, deviationId } = useParams();
  const { data: inspection } = useProjectVariationById(inspectionId);
  const getTitle = useCallback(() => {
    switch (location.pathname) {
      case routes.Egenkontroller.Pages.Edit.url(companyId!, inspectionId!):
        return lang.editSelfInspection;
      case routes.Filter.url(companyId!):
        return lang.filter;
      case routes.Egenkontroller.Pages.Deviations.Edit.url(
        companyId!,
        inspectionId!,
        deviationId!
      ):
        return lang.deviations;
      case routes.Egenkontroller.Pages.Deviations.ShowById.url(
        companyId!,
        inspectionId!,
        deviationId!
      ):
        return lang.deviations;
      case routes.Egenkontroller.Pages.Deviations.List.url(
        companyId!,
        inspectionId!
      ):
        return lang.deviations;
      case routes.Egenkontroller.Pages.Deviations.Create.url(
        companyId!,
        inspectionId!
      ):
        return lang.createDeviation;
      case routes.Deviation.Pages.Edit.url(companyId!, deviationId!):
        return lang.deviations;
      case routes.Assignments.show.url(companyId!, inspectionId!):
        return lang.assignments;
      default:
        return lang.egenkontrollerTitle;
    }
  }, [location.pathname, companyId, inspectionId, deviationId]);
  const isRouteWithPlusIcon = useMemo(() => {
    return [
      routes.SigningSession.Documents.url(companyId),
      routes.SigningSession.DocumentById.url(
        companyId,
        documentId,
        signingSessionId
      ),
      routes.Users.url(companyId!),
      routes.Groups.manage.url(companyId!),
      routes.Planner.url(companyId!),
      routes.Assignments.show.url(companyId!, inspectionId!),
      routes.Egenkontroller.Pages.Deviations.List.url(
        companyId!,
        inspectionId!
      ),
    ].includes(location.pathname);
  }, [location.pathname, companyId, inspectionId]);
  return (
    <div
      className={
        mobile
          ? "h-screen flex flex-col overflow-hidden"
          : "h-screen flex flex-col overflow-hidden bg-gray-100"
      }
    >
      <header
        className={
          "flex-shrink-0 h-16 w-full bg-PrimaryGreen grid grid-cols-5 text-White select-none"
        }
      >
        <div className="flex items-center justify-center">
          <i
            id="nav-back-button"
            className="fa-solid fa-arrow-left text-3xl cursor-pointer transition-all duration-150 ease-in-out hover:scale-110 active:scale-95 active:opacity-70"
            onClick={() => {
              if (
                location.pathname.includes(
                  routes.Egenkontroller.Pages.Deviations.Create.url(
                    companyId!,
                    inspectionId!
                  )
                )
              ) {
                nav(
                  routes.Egenkontroller.Pages.Deviations.List.url(
                    companyId!,
                    inspectionId!
                  )
                );
              } else {
                if (inspectionId) {
                  nav(
                    routes.Egenkontroller.Pages.Show.url(
                      companyId!,
                      inspectionId
                    )
                  );
                } else {
                  nav(-1);
                }
              }
            }}
          ></i>
        </div>
        <div className={"col-span-3 flex items-center justify-center"}>
          <div
            className={
              mobile
                ? "text-2xl text-center justify-center flex items-center"
                : "text-xl font-semibold text-center justify-center flex items-center"
            }
          >
            {
              <span
                id="header-title"
                className="text-xl font-semibold text-center justify-center flex items-center"
              >
                {getTitle()}
              </span>
            }
          </div>
        </div>
        <div className="items-center justify-center flex">
          {isRouteWithPlusIcon ? (
            <div className="flex items-center justify-center">
              <i
                id="plus-icon-add"
                className="fa-solid fa-plus text-4xl cursor-pointer text-red-500 transition-all duration-150 ease-in-out hover:scale-110 active:scale-95 active:opacity-70"
                title="Add"
              ></i>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <i
                id="icon-refresh"
                className="fa-solid fa-arrows-rotate text-4xl cursor-pointer transition-all duration-150 ease-in-out hover:scale-110 active:scale-95 active:opacity-70"
                onClick={() => {
                  queryClient.invalidateQueries();
                }}
              ></i>
            </div>
          )}
        </div>
      </header>
      <div
        className={
          mobile
            ? "flex-1 w-full overflow-auto"
            : "flex-1 bg-gray-50 p-4 shadow-inner overflow-auto"
        }
      >
        <Outlet />
      </div>
    </div>
  );
}
